import React, { createContext, useContext, useState, useEffect } from 'react';
import { ColorScheme } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { ThemeConfig, defaultThemeConfig, createMantineTheme } from './theme';

interface ThemeContextType {
  themeConfig: ThemeConfig;
  colorScheme: ColorScheme;
  updateTheme: (newConfig: ThemeConfig) => void;
  toggleColorScheme: (value?: ColorScheme) => void;
  theme: any; // MantineThemeOverride type
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  // Color scheme state
  const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
    key: "mantine-color-scheme",
    defaultValue: prefersDark ? "dark" : "light",
    getInitialValueInEffect: true,
  });

  // Theme config state
  const [themeConfig, setThemeConfig] = useLocalStorage({
    key: "mantine-theme-config",
    defaultValue: defaultThemeConfig,
    getInitialValueInEffect: true,
  });

  // Create the Mantine theme
  const [theme, setTheme] = useState(createMantineTheme(themeConfig, colorScheme));

  // Update theme when themeConfig or colorScheme changes
  useEffect(() => {
    setTheme(createMantineTheme(themeConfig, colorScheme));
  }, [themeConfig, colorScheme]);

  // Toggle color scheme
  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));

  // Update theme config
  const updateTheme = (newConfig: ThemeConfig) => {
    setThemeConfig(newConfig);
  };

  return (
    <ThemeContext.Provider value={{ themeConfig, colorScheme, updateTheme, toggleColorScheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
